import axios from 'axios';
import constants from 'config/constants';
import { formResMessage } from 'utils/commonFunc';
import { getReport } from './reports';

// Unofficial bills: money collected outside the official fee books, with a receipt (UB-<school>-<id>). A bill is
// either for a student (studentId; the backend fills payerName from the student) or for anyone else (no studentId,
// a free-text payerName). The backend only creates and reads them - there's no update or delete endpoint.
// Plain async calls (no reducer): only the admin-only Unofficial Bills page uses them.
const API = '/api/unofficial-bills';

const BASE = constants.API_BASE_URL;

// StudentCustomBillType values that make sense for a bill raised by hand. INVENTORY is left out: those bills come
// from issuing an inventory item, not from this form.
export const UNOFFICIAL_BILL_TYPES = [
  { label: 'Donation', value: 'DONATION' },
  { label: 'Development', value: 'DEVELOPMENT' },
  { label: 'Event', value: 'EVENT' },
  { label: 'Transport', value: 'TRANSPORT' },
  { label: 'Custom', value: 'CUSTOM' },
  { label: 'Other', value: 'OTHER' },
];

// The list endpoints, one per period. Each returns { fromDate, toDate, totalBills, totalAmount, bills }.
export const UNOFFICIAL_BILL_PERIODS = {
  ALL: 'ALL',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  CUSTOM: 'CUSTOM',
};

// period: one of UNOFFICIAL_BILL_PERIODS. date (any day of the week) for WEEKLY, month ('YYYY-MM') for MONTHLY,
// fromDate/toDate ('YYYY-MM-DD') for CUSTOM.
export const fetchUnofficialBillsService = async ({ schoolId, period, date, month, fromDate, toDate, token }) => {
  let path = '/collected';
  let params = { schoolId };
  if (period === UNOFFICIAL_BILL_PERIODS.WEEKLY) {
    path = '/report/weekly';
    params = { schoolId, date };
  } else if (period === UNOFFICIAL_BILL_PERIODS.MONTHLY) {
    const [year, monthNumber] = month.split('-');
    path = '/report/monthly';
    params = { schoolId, year: Number(year), month: Number(monthNumber) };
  } else if (period === UNOFFICIAL_BILL_PERIODS.CUSTOM) {
    path = '/report/custom';
    params = { schoolId, fromDate, toDate };
  }
  const res = await axios.get(`${BASE}${API}${path}${constants.makeParams(params)}`, constants.setHeader(token));
  return res.data;
};

export const createUnofficialBillService = async (postData, token) => {
  const res = await axios.post(`${BASE}${API}`, postData, constants.setHeader(token));
  return res.data;
};

// Opens the bill's PDF receipt in the app's report viewer (same modal the fee receipts use).
export const showUnofficialBillReceiptService =
  ({ schoolId, billId, receiptNumber, token }) =>
  async dispatch => {
    try {
      const res = await axios.get(`${BASE}${API}/${billId}/receipt${constants.makeParams({ schoolId })}`, {
        headers: constants.setHeader(token).headers,
        responseType: 'arraybuffer',
      });
      dispatch(getReport({ fileData: res.data, headingName: `Receipt ${receiptNumber || ''}`.trim() }));
      return true;
    } catch (err) {
      // The error body comes back as an ArrayBuffer because of responseType - decode it so the message is readable.
      const data = err?.response?.data;
      if (data instanceof ArrayBuffer) {
        try {
          err.response.data = JSON.parse(new TextDecoder('utf-8').decode(data));
        } catch {
          err.response.data = null;
        }
      }
      throw new Error(formResMessage(err) || 'Unable to open the receipt. Please try again.');
    }
  };
